import { Link } from "react-router-dom";
import { Clock, Eye } from "lucide-react";
import CategoryBadge from "@/components/CategoryBadge";
import { timeAgo, formatViews } from "@/lib/format";
import { cn } from "@/lib/utils";

/*
 * variant:
 *  - "hero"      : large lead story
 *  - "default"   : grid card with image
 *  - "horizontal": small card, image left, text right
 *  - "text"      : text only, for lists (most read)
 */
export default function ArticleCard({ article, variant = "default", index }) {
  if (!article) return null;
  const to = `/article/${article.slug}`;

  if (variant === "text") {
    return (
      <Link
        to={to}
        data-testid={`article-text-${article.slug}`}
        className="group flex gap-3 py-3 first:pt-0 last:pb-0"
      >
        {index != null && (
          <span className="font-display text-2xl font-extrabold leading-none text-slate-200">
            {index}
          </span>
        )}
        <div className="min-w-0">
          <h4 className="font-display text-[15px] font-semibold leading-snug text-slate-800 transition-colors group-hover:text-primary">
            {article.title}
          </h4>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <span>{timeAgo(article.published_at)}</span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={to}
        data-testid={`article-horizontal-${article.slug}`}
        className="group flex gap-4"
      >
        <div className="relative aspect-video w-28 shrink-0 overflow-hidden rounded-lg bg-slate-100 sm:w-32">
          <img
            src={article.image_url}
            alt={article.image_alt || article.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <CategoryBadge category={article.category} className="mb-1.5" asLink={false} />
          <h4 className="font-display text-sm font-bold leading-snug text-slate-900 transition-colors group-hover:text-primary sm:text-[15px]">
            {article.title}
          </h4>
          <span className="mt-1 block text-xs text-slate-500">
            {timeAgo(article.published_at)}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === "hero") {
    return (
      <Link
        to={to}
        data-testid={`article-hero-${article.slug}`}
        className="group relative block overflow-hidden rounded-2xl bg-slate-900"
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden lg:aspect-[16/9]">
          <img
            src={article.image_url}
            alt={article.image_alt || article.title}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
          <div className="flex items-center gap-2">
            <CategoryBadge category={article.category} asLink={false} />
            {article.is_breaking && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                Извънредно
              </span>
            )}
          </div>
          <h2 className="mt-3 max-w-3xl font-display text-2xl font-extrabold leading-tight text-white sm:text-3xl lg:text-4xl">
            {article.title}
          </h2>
          <p className="mt-3 hidden max-w-2xl text-sm text-slate-200 sm:line-clamp-2 sm:text-base">
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs font-medium text-slate-300">
            <span>{article.author}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {timeAgo(article.published_at)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  // default grid card
  return (
    <Link
      to={to}
      data-testid={`article-card-${article.slug}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-200/60",
      )}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
        <img
          src={article.image_url}
          alt={article.image_alt || article.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3">
          <CategoryBadge category={article.category} asLink={false} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-bold leading-snug text-slate-900 transition-colors group-hover:text-primary">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
          <span className="font-medium text-slate-600">{article.author}</span>
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {timeAgo(article.published_at)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {formatViews(article.views)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}
